import {
  ArrowsDownUpIcon,
  ArrowUpRightIcon,
  CalendarBlankIcon,
  CaretDownIcon,
  CaretUpIcon,
  CheckCircleIcon,
  CircleHalfIcon,
  FlagIcon,
  TrashIcon,
  UserCircleIcon,
} from "@foreday/ui/components/icons";
import { useRouter } from "@tanstack/react-router";
import { type FocusEvent, useEffect, useLayoutEffect, useRef } from "react";
import { TASK_PANEL_TITLE_ID } from "@/client/components/task-panel-slot";
import { clientCopy } from "@/client/copy";
import { type Command, registry } from "@/client/lib/keyboard/registry";
import { tasksCopy } from "@/features/tasks/copy";

const copy = tasksCopy.keys;
const ROW = "data-task-id";
/** On a list section's heading that takes focus, so the arrows start from it as from the h1. */
export const LIST_HEADING = "data-list-heading";

/** What a key does to the task that has focus; a key a list does not offer is not registered. */
export interface TaskKeyActions {
  readonly open?: (id: string) => void;
  readonly check?: (id: string) => void;
  readonly status?: (id: string) => void;
  readonly assign?: (id: string) => void;
  readonly due?: (id: string) => void;
  readonly priority?: (id: string) => void;
  readonly remove?: (id: string) => void;
  /** One place up or down among the rows shown, where the list keeps its own order. */
  readonly up?: (id: string) => void;
  readonly down?: (id: string) => void;
}

/** Each key that acts on the focused row, in the order the sheet lists them. */
const ON_ROW = [
  ["open", { icon: ArrowUpRightIcon, shortcut: "enter" }],
  ["check", { icon: CheckCircleIcon, shortcut: "x" }],
  ["status", { icon: CircleHalfIcon, shortcut: "s" }],
  ["assign", { icon: UserCircleIcon, shortcut: "a" }],
  ["due", { icon: CalendarBlankIcon, shortcut: "d" }],
  ["priority", { icon: FlagIcon, shortcut: "p" }],
  ["remove", { icon: TrashIcon, modifierShortcut: "mod+backspace" }],
  ["up", { icon: CaretUpIcon, modifierShortcut: "alt+arrowup" }],
  ["down", { icon: CaretDownIcon, modifierShortcut: "alt+arrowdown" }],
] as const satisfies readonly [
  keyof TaskKeyActions,
  Pick<Command, "icon" | "shortcut" | "modifierShortcut">,
][];

function focusedTask(): string | null {
  const active = document.activeElement;
  return active instanceof HTMLElement ? active.getAttribute(ROW) : null;
}

function rowOf(id: string): HTMLElement | null {
  return document.querySelector(`[${ROW}="${CSS.escape(id)}"]`);
}

/**
 * A row to focus once a list shows it: after the panel closes (on a phone the list mounts only
 * then), or after a move re-inserts the row. `order` is the list's order to wait past; a request
 * nobody fulfils lapses.
 */
let refocus: { id: string; order: string | null; at: number } | null = null;
const REFOCUS_MS = 2_000;

/**
 * Back on the task's row: now if a list shows it, and again once the list has moved past `order`,
 * since a moved row is re-inserted and drops its focus.
 */
export function focusTaskRow(id: string, order: string | null = null): void {
  const row = rowOf(id);
  row?.focus();
  refocus = row && order === null ? null : { id, order, at: Date.now() };
}

/** The task panel's pickers a key on a row opens. */
export type RowPicker = "status" | "assign" | "due" | "priority";

// Asked for from a row, taken by the panel once it shows that task.
let askedPicker: { readonly id: string; readonly picker: RowPicker } | null =
  null;
const pickerWatchers = new Set<() => void>();

/** The picker asked for on this task, once: the panel opens it and the ask is spent. */
export function takePicker(id: string): RowPicker | null {
  if (askedPicker?.id !== id) return null;
  const { picker } = askedPicker;
  askedPicker = null;
  return picker;
}

/** Runs `watch` whenever a row asks for a picker, for a panel already showing that task. */
export function watchPickers(watch: () => void): () => void {
  pickerWatchers.add(watch);
  return () => {
    pickerWatchers.delete(watch);
  };
}

/** S, A, D and P on a row: the task opens in the panel with that picker open. */
export function usePickerKeys(): Pick<TaskKeyActions, RowPicker> {
  const router = useRouter();
  const pick = (picker: RowPicker) => (id: string) => {
    if (id.startsWith("new-")) return;
    askedPicker = { id, picker };
    for (const watch of pickerWatchers) watch();
    void router.navigate({
      to: ".",
      search: (prev: object) => ({ ...prev, task: id }),
    });
  };
  return {
    status: pick("status"),
    assign: pick("assign"),
    due: pick("due"),
    priority: pick("priority"),
  };
}

/** Focus is somewhere in the task panel, outside the popups its pickers open. */
function inPanel(): boolean {
  const panel = document.querySelector(
    `aside[aria-labelledby="${TASK_PANEL_TITLE_ID}"]`,
  );
  return panel?.contains(document.activeElement) ?? false;
}

/**
 * ↑ and ↓ (or j and k) move focus through the rows in the order shown, from the page as well as
 * from a row; while the task panel shows one of these tasks, they move the panel to the next one
 * instead. The other keys act on the row that has focus.
 */
export function useTaskKeys(
  shown: readonly string[],
  actions: TaskKeyActions,
  /** Run when a row itself takes focus, to fetch what opening it reads. */
  preload?: (id: string) => void,
): (id: string) => {
  readonly [ROW]: string;
  readonly tabIndex: number;
  readonly onFocus?: (event: FocusEvent<HTMLElement>) => void;
} {
  const router = useRouter();
  const latest = useRef({ shown, actions, preload });
  // One handler for every row, so a row's props stay the same from one draw to the next.
  const onFocus = useRef((event: FocusEvent<HTMLElement>) => {
    const id = event.currentTarget.getAttribute(ROW);
    if (id && event.target === event.currentTarget)
      latest.current.preload?.(id);
  }).current;
  // Registered again when a key starts or stops being offered (a project archived or unarchived).
  const offered = ON_ROW.flatMap(([action]) =>
    actions[action] ? [action] : [],
  ).join(" ");
  useEffect(() => {
    latest.current = { shown, actions, preload };
  });

  useEffect(() => {
    const onRow = (
      pick: (actions: TaskKeyActions) => ((id: string) => void) | undefined,
      moves: boolean,
    ) => ({
      isVisible: () =>
        focusedTask() !== null && pick(latest.current.actions) !== undefined,
      run: () => {
        const id = focusedTask();
        if (!id) return;
        // A moved row is re-inserted in the DOM, which drops its focus.
        if (moves) focusTaskRow(id, latest.current.shown.join(" "));
        pick(latest.current.actions)?.(id);
      },
    });
    // A row still being saved has nothing on the server for the panel to open.
    const saved = (id: string) => !id.startsWith("new-");
    const steps = (open: string | undefined): open is string =>
      open !== undefined && latest.current.shown.includes(open);
    const group = clientCopy.palette.groups.onTask;
    const commands: Command[] = [
      {
        id: "task.move-focus",
        title: copy.move,
        group,
        icon: ArrowsDownUpIcon,
        shortcut: "arrowup arrowdown j k",
        // From the page, its h1 (focused on arrival), a section heading given focus, a row, or, while the task panel shows a task of
        // this list, the panel or a control in a row; never from another side panel or a control the
        // arrows already drive, whose popups render outside the panel.
        isVisible: () =>
          focusedTask() !== null ||
          document.activeElement === document.body ||
          document.activeElement?.tagName === "H1" ||
          document.activeElement?.hasAttribute(LIST_HEADING) === true ||
          ((inPanel() || document.activeElement?.closest(`[${ROW}]`) != null) &&
            steps(router.state.location.search.task)),
        run: (ctx) => {
          const { task: open } = router.state.location.search;
          const step = ctx.key === "arrowup" || ctx.key === "k" ? -1 : 1;
          const at = (list: readonly string[], from: string | null) => {
            const index = list.indexOf(from ?? "");
            return index === -1
              ? -1
              : Math.min(Math.max(index + step, 0), list.length - 1);
          };
          const openable = latest.current.shown.filter(saved);
          if (steps(open)) {
            const next = openable[at(openable, open)];
            // Held on the first or last task: the same location would load the route again.
            if (!next || next === open) return;
            rowOf(next)?.scrollIntoView({ block: "nearest" });
            // Replaced, so Back closes the panel rather than stepping back through each task.
            void router.navigate({
              to: ".",
              search: (prev: object) => ({ ...prev, task: next }),
              replace: true,
            });
            return;
          }
          const list = latest.current.shown;
          const next = list[at(list, focusedTask())] ?? list[0];
          if (next) rowOf(next)?.focus();
        },
      },
      ...ON_ROW.filter(([action]) => offered.split(" ").includes(action)).map(
        ([action, keys]): Command => ({
          id: `task.${action}`,
          title: copy[action],
          group,
          ...keys,
          ...onRow((all) => all[action], action === "up" || action === "down"),
        }),
      ),
    ];
    const taken = commands.map((command) => registry.register(command));
    return () => {
      for (const take of taken) take();
    };
  }, [offered, router]);

  useLayoutEffect(() => {
    if (!refocus || !shown.includes(refocus.id)) return;
    if (Date.now() - refocus.at > REFOCUS_MS) refocus = null;
    else if (refocus.order !== shown.join(" ")) {
      rowOf(refocus.id)?.focus();
      refocus = null;
    }
  });

  // The first row is the tab stop; arrows move focus from there.
  return (id) => ({
    [ROW]: id,
    tabIndex: id === shown[0] ? 0 : -1,
    onFocus: preload ? onFocus : undefined,
  });
}
